import { Grid, Autocomplete, Box, IconButton, TextField, Typography, InputAdornment } from '@mui/material'
import cn from 'classnames'
import { useState } from 'react'
import type { Map as LeafletMap } from 'leaflet'
import { GeoJsonObject } from 'geojson'
import { useEmitters } from '../../../contexts/emitters.context'
import { IFrequencyAssignment, IExercise } from '../../../types/exercise.type'
import FrequencyRangeInput from '../../../components/shared/FrequencyFilters/FrequencyInputs'
import FrequencyPicker from '../../../components/shared/FrequencyFilters/FrequencyPicker'
import DateRangePicker from '../../../components/shared/DateRangePicker'
import MultiRangeSlider from '../../../components/shared/MultiRangeSlider'
import KMZUploader from './kmz-uploader'
import ArrowButton from '@mui/icons-material/ArrowCircleRightOutlined'
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined'
import RemoveButton from '@mui/icons-material/DeleteOutline'
import { styled, useTheme } from '@mui/material/styles'
import Button from '@mui/material/Button'
import scss from './styles.module.scss'

interface IProps {
  onKmlUpload: (dataSource: GeoJsonObject) => void
  mapRef: React.MutableRefObject<LeafletMap | null>
  showScaleCircle: boolean
  onToggleScaleCircle: () => void
}

function getKeys<T>() {
  return <K extends keyof T>(keys: K[]) => keys
}

const exerciseKeys = getKeys<IFrequencyAssignment>()([
  'request_frequency_group_id',
  'request_frequency_created_at',
  'request_frequency_supplementary_details',
  'request_frequency_single_id',
  'request_frequency_single_start_indicator',
  'request_frequency_single_start_freq',
  'request_frequency_single_end_indicator',
  'request_frequency_single_end_freq',
  'request_location_longitude_deg',
  'request_location_longitude_min',
  'request_location_longitude_sec',
  'request_location_latitude_deg',
  'request_location_latitude_min',
  'request_location_latitude_sec',
  'request_location_radius',
  'request_location_transmitter_location',
  'state_region_type_name',
  'state_region_type_code',
  'major_function_511_name',
  'intermediate_function_512_name',
  'detailed_function',
  'location_afc',
  'location_afc_code',
  'location_afc_coord_note',
  'number_of_frequencies',
  'request_assigned_frequency_list_serial_number',
  'f_114',
  'id_override',
  'exercise_name',
])

const WhiteButton = styled(Button)({
  backgroundColor: '#fff',
  color: '#000',
  border: '2px solid rgba(0, 0, 0, 0.2)',
  backgroundClip: 'padding-box',
  '&:hover': {
    backgroundColor: '#fff',
    color: '#000',
    border: '2px solid rgba(0, 0, 0, 0.2)',
    backgroundClip: 'padding-box',
  },
})

const SideBar: React.FC<IProps> = ({ onKmlUpload, mapRef, showScaleCircle, onToggleScaleCircle }) => {
  const theme = useTheme()

  const {
    filters,
    frequencyRange,
    startDate,
    endDate,
    areUploadsVisible,
    exercisesToFilter,
    getExercises,
    clearFilters,
    handleFilterChange,
    handleFilterRemove,
    handleDateRangeChange,
    handleFrequencyRangechange,
    toggleUploadVisibility,
    frequencyAssignments,
    setDisplayingFrequencyAssignmentIds,
  } = useEmitters()
  const SIDEBAR_OPEN_LEFT = 458 // 400px box + 58px arrow offset
  const SIDEBAR_CLOSED_LEFT = 58 // 58px arrow offset only

  const [isOpen, setIsOpen] = useState(false)
  const [frequencyLimits, setFrequencyLimits] = useState({
    min: 0,
    max: 10000,
  })

  const handleInputChange = (index: number, key: keyof IFrequencyAssignment, value: string) => {
    const parsedValue = isNaN(Number(value)) ? value : Number(value)
    handleFilterChange(index, key, parsedValue)
  }

  return (
    <div
      className={`${scss.sidebar} sub-container`}
      style={{
        background: theme.palette.background.default,
      }}
    >
      <div className={scss.container}>
        <div className={cn(scss.box, { [scss.closed]: !isOpen })}>
          <h4>Filter by:</h4>

          <div className={scss.frequencies}>
            <p>Frequency:</p>
            <div className={scss.frequencySlider}>
              <MultiRangeSlider
                min={frequencyLimits.min}
                max={frequencyLimits.max}
                values={frequencyRange ? [frequencyRange.min, frequencyRange.max] : undefined}
                onChange={handleFrequencyRangechange}
              />
            </div>
            <FrequencyRangeInput values={frequencyRange} onChange={handleFrequencyRangechange} />
            <FrequencyPicker
              values={frequencyLimits}
              onChange={(values) => {
                setFrequencyLimits(values)
                handleFrequencyRangechange(values)
              }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p>Date:</p>
            {!!exercisesToFilter.length && (
              <Autocomplete
                fullWidth
                options={exercisesToFilter}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => <TextField {...params} label="Select Exercise to filter date" />}
                onChange={(_, newValue: IExercise | null) => {
                  if (newValue) {
                    // We have to play some sketchy type games here
                    // because IExercise is declared with Dates,
                    // but axios only gives us the JSON strings
                    // and doesn't know anything about converting them to Dates.
                    // Since we share getAllExercises with the dashboard,
                    // I'm not sure if it's safe to change the type
                    // or the request-form service.
                    handleDateRangeChange(
                      (newValue.start_date as unknown as string).split('T')[0],
                      (newValue.end_date as unknown as string).split('T')[0],
                    )
                  }
                }}
              />
            )}
            <DateRangePicker
              initialStartDate={startDate || undefined}
              initialEndDate={endDate || undefined}
              onDateRangeChange={(startDate, endDate) =>
                handleDateRangeChange(
                  startDate ? startDate.toISOString().split('T')[0] : null,
                  endDate ? endDate.toISOString().split('T')[0] : null,
                )
              }
            />
          </div>

          <div>
            <div className={scss.label}>
              <Typography>Custom field:</Typography>
              {filters.length <= 10 && (
                <IconButton onClick={() => handleFilterChange(filters.length, 'request_frequency_group_id', '')}>
                  <AddCircleOutlineOutlinedIcon />
                </IconButton>
              )}
            </div>
            <Grid container spacing={2}>
              {filters.map((filter, index) => (
                <Grid container item spacing={2} key={index}>
                  <Grid item xs={6}>
                    <Autocomplete
                      options={exerciseKeys}
                      getOptionLabel={(option) => String(option)}
                      renderInput={(params) => <TextField {...params} label="Select property to filter by" />}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      value={String(filter.value)}
                      onChange={(e) => handleInputChange(index, filter.key, e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => handleFilterRemove(index)}>
                              <RemoveButton />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              ))}
            </Grid>
            <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
              <Button variant="outlined" onClick={getExercises}>
                Filter
              </Button>
              <Button variant="outlined" onClick={clearFilters}>
                Clear
              </Button>
            </div>
          </div>

          <div>
            <Typography>Frequency Assignments:</Typography>
            <Autocomplete
              multiple
              options={frequencyAssignments.map((freq) => freq.id_override).sort()}
              getOptionLabel={(id_override) => id_override}
              renderInput={(params) => <TextField {...params} label="Select assignments to display" />}
              onChange={(_, id_overrides) => {
                setDisplayingFrequencyAssignmentIds(new Set(id_overrides))
              }}
            />
          </div>

          <Box className={cn(scss.arrowButton, { [scss.closed]: !isOpen })} onClick={() => setIsOpen((prev) => !prev)}>
            <ArrowButton className={scss.icon} />
          </Box>
          <div
            className={scss.heatmapButton}
            style={{
              left: isOpen ? SIDEBAR_OPEN_LEFT : SIDEBAR_CLOSED_LEFT,
              top: 9,
            }}
          >
            <WhiteButton variant="outlined" onClick={toggleUploadVisibility}>
              {areUploadsVisible ? 'Hide' : 'Show'} uploads
            </WhiteButton>
            <WhiteButton variant="outlined" onClick={onToggleScaleCircle}>
              {showScaleCircle ? 'Hide' : 'Show'} scale circle
            </WhiteButton>
          </div>
          <div
            className={scss.heatmapButton}
            style={{
              left: isOpen ? SIDEBAR_OPEN_LEFT : SIDEBAR_CLOSED_LEFT,
              top: 50,
            }}
          >
            <div
              className={`leaflet-control-zoom leaflet-bar leaflet-control ${scss.leafletBar}`}
              style={{ margin: 0 }}
            >
              <button
                className="leaflet-control-zoom-in"
                aria-label="Zoom in"
                onClick={(e) => {
                  e.preventDefault()
                  mapRef.current?.zoomIn()
                }}
              >
                +
              </button>
              <button
                className="leaflet-control-zoom-out"
                aria-label="Zoom out"
                onClick={(e) => {
                  e.preventDefault()
                  mapRef.current?.zoomOut()
                }}
              >
                −
              </button>
            </div>
          </div>

          <div style={{ marginTop: 'auto' }}>
            <KMZUploader onKMLLoaded={onKmlUpload} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default SideBar
